/* ============================================================
   FF Product Page Layout
   ------------------------------------------------------------
   1. GALLERY GRID - fills the empty column under the product
      photos on desktop with the rest of the photos at full
      width, one at a time, plus a `See all N photos` button.
      Set gallery.columns to 2 for a two-across grid instead.

   2. CONTENT MOVE - lifts the Description / Features / FAQ
      panels out of the full-width tab strip lower down the page
      and puts them in the right-hand column under the buy box,
      as accordions. The photo column then pins so it stays
      beside you while you read.

   Nothing here touches Flickity or the theme accordion code.
   The carousel, thumbnails, zoom viewer and variant switching
   all keep working through the theme own controls.

   Turn either half off in the CFG block below.
   Added August 2026.
   ============================================================ */

(function () {
  'use strict';

  if (window.__ffProductPage) return;
  window.__ffProductPage = true;

  var CFG = {

    desktopMin: 1000,      // Focal switches to two columns here

    gallery: {
      enabled: true,
      columns: 1,          // 1 = full width, one photo at a time
      gap: 12,             // must match the gap in the css file
      minPhotos: 3,        // fewer than this and a grid is not worth it
      buttonReserve: 64,   // vertical room kept for the See all button
      zoomDelay: 90        // ms to let the carousel settle before zooming
    },

    layout: {
      enabled: true,
      stickyMedia: true,
      stickyGap: 24,
      // Which panel starts open. 0 opens the first one (Description),
      // -1 starts them all closed.
      openOnDesktop: 0,
      openOnMobile: 0      // Description open, the rest closed
    }
  };

  var teardown   = [];
  var savedPanels = null;   // survives Focal re-rendering the product block
  var lastMode    = null;   // desktop or mobile, for panel state changes

  function qs(sel, root) { return (root || document).querySelector(sel); }
  function qsa(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }
  function isDesktop() { return window.innerWidth >= CFG.desktopMin; }
  function isImage(el) { return el.getAttribute('data-media-type') === 'image'; }

  function debounce(fn, wait) {
    var t;
    return function () { clearTimeout(t); t = setTimeout(fn, wait); };
  }

  function cleanUp() {
    while (teardown.length) {
      try { teardown.pop()(); } catch (e) { /* nothing to do */ }
    }
  }


  /* ---------- content move ---------------------------------- */

  function moveContent() {
    if (!CFG.layout.enabled) return;

    var info = qs('.product__info');
    if (!info) return;

    var panels = savedPanels || qs('.product-tabs');
    if (!panels) return;
    if (info.contains(panels)) return;          // already in place

    if (!savedPanels) {
      // First run only: turn the tab strip into a stack of accordions.
      var origin = panels.closest('.shopify-section');

      document.documentElement.classList.add('ff-pl-init');

      // Remove, do not hide. The tabs-nav custom element is the controller
      // for these panels: left in the page it keeps forcing its active panel
      // back open every time a shopper closes it.
      qsa('tabs-nav, .tabs-nav', panels).forEach(function (nav) {
        nav.parentNode.removeChild(nav);
      });

      buildAccordions(panels);

      panels.classList.add('ff-pl-panels');

      info.appendChild(panels);
      savedPanels = panels;

      // Collapse the section the panels came from, but only if moving
      // them left it genuinely empty.
      if (origin && !origin.innerText.trim() && !qs('.shopify-block', origin)) {
        origin.classList.add('ff-pl-emptied');
      }

      setPanelStates(true);
      guardPanelVisibility(panels);

      requestAnimationFrame(function () {
        document.documentElement.classList.remove('ff-pl-init');
      });
      return;
    }

    // Focal replaced the product block and took our panels with it.
    info.appendChild(savedPanels);
  }

  // If anything ever puts the tab-mode hidden attribute back on a panel,
  // strip it. A hidden panel in accordion mode is just a missing section.
  function guardPanelVisibility(panels) {
    if (!window.MutationObserver) return;
    var obs = new MutationObserver(function (records) {
      records.forEach(function (r) {
        if (r.target.hasAttribute('hidden')) r.target.removeAttribute('hidden');
      });
    });
    qsa('.product-tabs__tab-item-wrapper', panels).forEach(function (wrap) {
      obs.observe(wrap, { attributes: true, attributeFilter: ['hidden'] });
    });
  }

  // Our own accordion, rather than driving the theme one.
  //
  // The theme accordion is half of a tab widget: something in that widget
  // kept re-opening a panel the moment it was closed, and hiding or removing
  // the tab strip did not stop it. So we leave the theme collapsible
  // permanently open as far as it is concerned, wrap it, and do the opening
  // and closing ourselves. Nothing can fight us over it.
  function buildAccordions(panels) {
    qsa('.product-tabs__tab-item-wrapper', panels).forEach(function (wrap, i) {
      if (wrap.classList.contains('ff-acc')) return;

      wrap.removeAttribute('hidden');            // tab mode hides all but one

      var themeBtn = qs('.collapsible-toggle', wrap);
      var content  = qs('.collapsible', wrap);
      if (!content) return;

      var title = themeBtn ? themeBtn.textContent.trim() : '';
      if (!title) title = 'More information';

      content.setAttribute('open', '');          // never let the theme collapse it
      if (themeBtn) themeBtn.classList.add('ff-pl-hide');

      var head = document.createElement('button');
      head.type = 'button';
      head.className = 'ff-acc__head';
      head.textContent = title;
      head.setAttribute('aria-expanded', 'false');

      var panel = document.createElement('div');
      panel.className = 'ff-acc__panel';
      panel.id = 'ff-acc-panel-' + i;

      var inner = document.createElement('div');
      inner.className = 'ff-acc__panel-inner';
      panel.appendChild(inner);

      head.setAttribute('aria-controls', panel.id);

      wrap.insertBefore(head, wrap.firstChild);
      wrap.insertBefore(panel, head.nextSibling);
      inner.appendChild(content);

      wrap.classList.add('ff-acc');

      head.addEventListener('click', function () {
        setOpen(wrap, !wrap.classList.contains('is-open'));
      });
    });
  }

  function setOpen(wrap, open) {
    wrap.classList.toggle('is-open', open);
    var head = qs('.ff-acc__head', wrap);
    if (head) head.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  function setPanelStates(force) {
    if (!savedPanels) return;

    var mode = isDesktop() ? 'desktop' : 'mobile';
    if (!force && mode === lastMode) return;    // never fight the shopper own clicks
    lastMode = mode;

    var openIndex = isDesktop() ? CFG.layout.openOnDesktop : CFG.layout.openOnMobile;

    qsa('.ff-acc', savedPanels).forEach(function (wrap, i) {
      setOpen(wrap, i === openIndex);
    });
  }

  // The zoom viewer lives inside the photo column. Making that column sticky
  // gives it its own stacking layer, so the viewer can no longer paint over
  // the header or the sticky Add to Cart bar, and the close button ends up
  // underneath them. Hide that page furniture while the viewer is open.
  function watchZoom() {
    var zoom = qs('product-image-zoom');
    if (!zoom || !window.MutationObserver || zoom.__ffZoomWatched) return;
    zoom.__ffZoomWatched = true;

    function sync() {
      document.documentElement.classList.toggle(
        'ff-zoom-open', zoom.classList.contains('pswp--open'));
    }

    new MutationObserver(sync).observe(zoom, { attributes: true, attributeFilter: ['class'] });
    sync();
  }


  /* ---------- sticky photo column --------------------------- */

  function clearStickyBlockers(media) {
    var el = media.parentElement, depth = 0;
    while (el && el !== document.body && depth < 8) {
      var ov = getComputedStyle(el).overflow;
      if (ov && ov !== 'visible') el.classList.add('ff-pl-overflow-fix');
      el = el.parentElement;
      depth++;
    }
  }

  function applySticky() {
    if (!CFG.layout.enabled || !CFG.layout.stickyMedia) return;

    var product = qs('.product');
    var media   = qs('product-media.product__media');
    if (!product || !media) return;

    if (!isDesktop()) {
      product.classList.remove('ff-pl-sticky');
      return;
    }

    product.classList.add('ff-pl-sticky');
    clearStickyBlockers(media);

    var root       = getComputedStyle(document.documentElement);
    var headerH    = parseFloat(root.getPropertyValue('--header-height')) || 0;
    var headerSticky = parseFloat(root.getPropertyValue('--enable-sticky-header')) === 1;
    var offset     = CFG.layout.stickyGap + (headerSticky ? headerH : 0);

    // Taller than the screen gives a negative top, which is what makes
    // it scroll first and pin once its bottom reaches the bottom.
    var top = Math.min(offset, window.innerHeight - media.offsetHeight - CFG.layout.stickyGap);
    media.style.setProperty('--ff-sticky-top', Math.round(top) + 'px');
  }


  /* ---------- gallery grid ---------------------------------- */

  function buildGallery() {
    if (!CFG.gallery.enabled) return;

    var mediaEl = qs('product-media.product__media');
    var info    = qs('.product__info');
    if (!mediaEl || !info) return;
    if (qs('.ff-gg', mediaEl)) return;               // already built

    var items = qsa('.product__media-item', mediaEl).filter(isImage);
    if (items.length < CFG.gallery.minPhotos) return;

    cleanUp();

    var wrap = document.createElement('div');
    wrap.className = 'ff-gg';

    var grid = document.createElement('div');
    grid.className = 'ff-gg__grid';
    grid.style.setProperty('--ff-gg-cols', CFG.gallery.columns);
    wrap.appendChild(grid);

    // Tell the browser roughly how wide each photo will render, so it picks
    // a sensibly sized file rather than the largest one.
    var sizeHint = CFG.gallery.columns === 1
      ? '(min-width: 1000px) 600px, 100vw'
      : '(min-width: 1000px) 300px, 50vw';

    // Everything after the lead image. The lead image is already showing
    // full size at the top, so repeating it directly below looks odd.
    items.slice(1).forEach(function (item) {
      var img = qs('img', item);
      if (!img) return;

      var tile = document.createElement('button');
      tile.type = 'button';
      tile.className = 'ff-gg__tile';
      tile.setAttribute('data-media-id', item.getAttribute('data-media-id') || '');
      tile.setAttribute('aria-label', 'View larger: ' + (img.getAttribute('alt') || 'product photo'));

      // cloneNode(false) carries src, srcset, width and height across, so
      // the browser picks a correctly sized file for the small tile.
      var clone = img.cloneNode(false);
      clone.removeAttribute('class');
      clone.removeAttribute('style');
      clone.setAttribute('loading', 'lazy');
      clone.setAttribute('decoding', 'async');
      clone.setAttribute('sizes', sizeHint);

      tile.appendChild(clone);
      grid.appendChild(tile);
    });

    if (!grid.children.length) return;

    var more = document.createElement('button');
    more.type = 'button';
    more.className = 'ff-gg__more';
    more.hidden = true;
    wrap.appendChild(more);

    mediaEl.appendChild(wrap);

    var expanded = false;

    // How much room the grid is allowed to fill: down to the bottom of the
    // text column. With the description moved in, that column is long enough
    // to take every photo, so they all show and then the column pins.
    function roomForGrid() {
      return info.getBoundingClientRect().bottom
           - grid.getBoundingClientRect().top
           - CFG.gallery.buttonReserve;
    }

    function fit() {
      if (!isDesktop()) return;

      var tiles = qsa('.ff-gg__tile', grid);
      if (!tiles.length) return;

      tiles.forEach(function (t) { t.hidden = false; });
      wrap.hidden = false;

      if (expanded) {
        more.hidden = false;
        more.textContent = 'Show fewer photos';
        applySticky();
        return;
      }

      var rowHeight = tiles[0].offsetHeight + CFG.gallery.gap;
      if (!rowHeight || rowHeight < 20) return;      // images not measured yet

      var available = roomForGrid();

      // Not enough room to be worth filling. Leave the page alone rather
      // than making the photo column taller than it needs to be.
      if (available < rowHeight) {
        wrap.hidden = true;
        applySticky();
        return;
      }

      var rows    = Math.floor(available / rowHeight);
      var visible = rows * CFG.gallery.columns;

      if (visible >= tiles.length) {
        more.hidden = true;
      } else {
        tiles.forEach(function (t, i) { t.hidden = i >= visible; });
        more.hidden = false;
        more.textContent = 'See all ' + items.length + ' photos';
      }

      applySticky();
    }

    more.addEventListener('click', function () {
      expanded = !expanded;
      fit();
      if (!expanded) wrap.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });

    grid.addEventListener('click', function (event) {
      var tile = event.target.closest ? event.target.closest('.ff-gg__tile') : null;
      if (!tile) return;

      var id = tile.getAttribute('data-media-id');

      // Drive the carousel through its own thumbnail button rather than
      // reaching into Flickity, so this survives theme updates.
      var thumb = qsa('.product__thumbnail-item').filter(function (b) {
        return b.getAttribute('data-media-id') === id;
      })[0];
      if (thumb) thumb.click();

      var zoom = qs('.product__zoom-button');
      if (zoom) {
        setTimeout(function () { zoom.click(); }, CFG.gallery.zoomDelay);
      } else {
        var top = qs('.product__media-list-wrapper');
        if (top) top.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    });

    var refit = debounce(fit, 150);

    window.addEventListener('resize', refit);
    teardown.push(function () { window.removeEventListener('resize', refit); });

    window.addEventListener('load', fit);
    teardown.push(function () { window.removeEventListener('load', fit); });

    qsa('img', grid).forEach(function (im) {
      if (!im.complete) im.addEventListener('load', refit, { once: true });
    });

    // Reviews, upsell apps and metafield content all land late and change
    // how tall the text column is. Watch it rather than guessing.
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(refit);
      ro.observe(info);
      teardown.push(function () { ro.disconnect(); });
    } else {
      [400, 1200, 2500].forEach(function (ms) { setTimeout(fit, ms); });
    }

    requestAnimationFrame(fit);
  }


  /* ---------- wiring ---------------------------------------- */

  function run() {
    moveContent();
    buildGallery();
    applySticky();
    watchZoom();
  }

  var onResize = debounce(function () {
    setPanelStates(false);
    applySticky();
  }, 150);

  function start() {
    run();

    var host = qs('product-rerender');
    if (host && window.MutationObserver && !host.__ffWatched) {
      host.__ffWatched = true;
      // Focal swaps the whole product block on a variant change, which takes
      // the moved panels and the grid with it. Put them both back.
      new MutationObserver(debounce(run, 120)).observe(host, { childList: true });
    }

    window.addEventListener('resize', onResize);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Theme editor: sections re-render on every settings change.
  document.addEventListener('shopify:section:load', function () {
    setTimeout(run, 120);
  });
})();
